'use client';

import { useState, useRef, useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Building2, ArrowLeft, ArrowRight } from 'lucide-react';
import { StepsIndicator } from '@/components/glitch-ui/steps-indicator';
import {
  CampaignLocations,
  CampaignLocationsRef,
} from '@/components/campaign/onboarding/campaign-onboarding-locations';
import { useCreateBrandCampaignMutation, businessQueryOptions, campaignQueryOptions } from '@/lib/query-options';
import { setCampaignProximityTargets } from '@/lib/apis';
import { Location, type CampaignProximityInput } from '@/lib/types';
import { generateCampaignSlug } from '@/lib/nanoid-utils';
import { useToast } from '@/hooks/useToast';
import { mapErrorToUserMessage } from '@/lib/error-messages';
import useLoadingOverlay from '@/hooks/useLoadingOverlay';
import { useRefetchCampaigns } from '@/hooks/useRefetchCampaign';
import { useAtomValue } from 'jotai';
import { organizationDataAtom } from '@/store';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';

export const Route = createFileRoute('/_authenticated/new/_layout/campaign/brand')({
  component: BrandCampaign,
});

function BrandCampaign() {
  const { toast } = useToast();
  const posthog = usePostHog();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const organizationData = useAtomValue(organizationDataAtom);

  const { data: business, isLoading: isLoadingBusinesses } = useQuery({
    ...businessQueryOptions(organizationData?.id || 0),
    enabled: !!organizationData?.id,
  });

  // Initialize locations from business defaults
  const [locations, setLocations] = useState<Location[]>([]);
  // #5444: radii chosen on the wizard's Radius tab, saved post-create.
  const [radii, setRadii] = useState<CampaignProximityInput[]>([]);
  const [stepValid, setStepValid] = useState(false);
  const campaignLocationsRef = useRef<CampaignLocationsRef>(null);

  const { refetchWithDelayedUpdates } = useRefetchCampaigns();
  const createBrandCampaignMutation = useCreateBrandCampaignMutation();
  useLoadingOverlay(createBrandCampaignMutation.isPending);

  // Set default locations from business when available
  useEffect(() => {
    if (business?.target_locations && locations.length === 0) {
      setLocations(business.target_locations);
    }
  }, [business?.target_locations]);

  // Track page view
  useEffect(() => {
    if (business) {
      posthog?.capture('brand_campaign_creation_started', {
        business_id: business.id,
        brand_name: business.brand_name,
      });
    }
  }, [business?.id]);

  const handleSubmit = async () => {
    if (!business?.id) {
      return;
    }

    // Approve locations first
    const locationsApproved = campaignLocationsRef.current?.approveLocations();
    if (!locationsApproved) {
      return;
    }

    try {
      posthog?.capture('brand_campaign_creation_submitted', {
        business_id: business.id,
        location_count: locations.length,
      });

      const campaign = await createBrandCampaignMutation.mutateAsync({
        slug: generateCampaignSlug(),
        website_url: business.business_url,
        business_desc: '', // Will use scraped data from business URL
        goal: 'brand',
        target_locations: locations,
        campaign_goal_text: 'Brand awareness campaign',
        budget: 0,
        organization_id: organizationData?.id,
        business_id: business.id,
      });

      // #5444: persist the wizard's Radius-tab selection on the new campaign.
      if (radii.length > 0 && campaign.id) {
        try {
          await setCampaignProximityTargets(campaign.id, radii);
        } catch {
          toast({
            description: 'Campaign created, but saving radius targets failed — set them in campaign settings.',
            variant: 'destructive',
          });
        }
      }

      await refetchWithDelayedUpdates();

      posthog?.capture('brand_campaign_creation_completed', {
        campaign_id: campaign.id,
        campaign_slug: campaign.slug,
      });


      if (campaign.slug) {
        // Seed the single-campaign cache so the detail route's loader resolves the
        // just-created campaign immediately. Without this, the loader's fetch can
        // 404 on the not-yet-queryable new campaign and redirect to /campaigns,
        // leaving the previously-open campaign selected instead of the new one.
        // campaignQueryOptions' refetchInterval fills in the real data (name, ads)
        // as generation completes.
        queryClient.setQueryData(campaignQueryOptions(campaign.slug).queryKey, campaign);
        navigate({
          to: `/campaigns/google/$campaignslug`,
          params: { campaignslug: campaign.slug },
        });
      }
    } catch (error: unknown) {
      const rawMessage = error instanceof Error ? error.message : 'Failed to create Brand Campaign';
      const errorMessage = mapErrorToUserMessage(rawMessage);
      posthog?.capture('brand_campaign_creation_failed', {
        error_message: rawMessage,
      });
      toast({
        description: errorMessage,
        variant: 'destructive',
      });
      console.error('Failed to create brand campaign', error);
    }
  };

  // Show loading state while fetching businesses
  if (isLoadingBusinesses) {
    return (
      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 max-w-5xl w-full space-y-6 m-auto pt-8 pb-16">
        <div className="flex justify-center">
          <div className="w-[60%]">
            <Card className="p-6">
              <div className="flex items-center justify-center py-12">
                <p className="text-gray-500">Loading...</p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Show empty state if no brand exists
  if (!business) {
    return (
      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 max-w-5xl w-full space-y-6 m-auto pt-8 pb-16">
        <div className="flex justify-center">
          <div className="w-[60%]">
            <Card className="rounded-lg border bg-white">
              <CardContent className="p-6">
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Building2 className="h-12 w-12 text-gray-900 mb-4" strokeWidth={1} />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Create Your Brand First</h3>
                  <p className="text-gray-700 mb-6">You need to create a brand before creating campaigns.</p>
                  <Button
                    onClick={() => navigate({ to: '/new/brand' })}
                    size="lg"
                    className="w-[280px] py-6 bg-[#290BD9] hover:bg-[#2309C5] text-white shadow-lg flex items-center justify-center space-x-2"
                  >
                    <span>CREATE BRAND</span>
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 max-w-5xl w-full space-y-6 m-auto pt-8 pb-16">
      <StepsIndicator currentStep={1} totalSteps={1} />
      <div className="flex justify-center">
        <div className="w-full">
          <h1 className="text-3xl font-bold text-center mb-2">Create a Brand Campaign</h1>
          <p className="text-gray-700 text-center mb-8">
            Add the countries, regions, states, or cities where you want your ads to appear.
          </p>
          <Card className="p-6">
            <CampaignLocations
              ref={campaignLocationsRef}
              onNext={() => {}}
              setLocations={setLocations}
              setStepValid={setStepValid}
              locations={locations}
              businessId={business.id}
              candidates={business.target_locations ?? []}
              setRadii={setRadii}
            />
          </Card>

          <div className="mx-auto flex w-full max-w-[614px] justify-between items-center pt-6">
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate({ to: '/new/campaign' })}
              className="w-[216px] py-6 flex items-center justify-center space-x-2 text-gray-700 hover:text-gray-900 border-gray-200 bg-white hover:bg-gray-50"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>BACK</span>
            </Button>

            <Button
              size="lg"
              disabled={!stepValid || createBrandCampaignMutation.isPending}
              onClick={handleSubmit}
              className="w-[216px] py-6 bg-[#290BD9] hover:bg-[#2309C5] shadow-lg flex items-center justify-center space-x-2"
            >
              <span>CREATE</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
